import {
  Employee,
  Workstream,
  WorkstreamDependencyType,
  WorkstreamEdit,
  WorkstreamEmployee,
} from 'client';

type WorkstreamEditSource = Pick<
  Workstream,
  'id' | 'name' | 'start_date' | 'end_date' | 'status_due' | 'status_cadence' | 'privacy'
> &
  Partial<
    Pick<
      Workstream,
      | 'pillar'
      | 'status_due_date'
      | 'status_update_needed'
      | 'links'
      | 'private_employees'
      | 'private_teams'
      | 'dependencies'
      | 'action_type'
      | 'description'
    >
  > & {
    employee_associations: WorkstreamEmployee[];
  };

export const toWorkstreamEditPayload = (workstream: WorkstreamEditSource): WorkstreamEdit => ({
  id: workstream.id,
  name: workstream.name,
  start_date: workstream.start_date,
  end_date: workstream.end_date,
  status_due: workstream.status_due,
  status_cadence: workstream.status_cadence,
  pillar: workstream.pillar ?? undefined,
  status_due_date: workstream.status_due_date,
  status_update_needed: workstream.status_update_needed,
  employee_associations: (workstream.employee_associations ?? []).map(({employee, dri}) => ({
    user_id: employee.user_id,
    dri,
  })),
  links: (workstream.links ?? []).map(({id, icon, text, url}) => ({
    id,
    icon,
    text,
    url,
  })),
  privacy: workstream.privacy,
  description: workstream.description,
  private_employees: workstream.private_employees?.map(({user_id}) => user_id),
  private_teams: workstream.private_teams?.map((team) =>
    typeof team === 'string' ? team : team.team_id
  ),
  dependencies: (workstream.dependencies ?? []).map(({dependent, order}) => ({
    dependent_id: dependent.id,
    type: WorkstreamDependencyType.DEPENDS_ON,
    order,
  })),
  action_type: workstream.action_type,
});

export const replaceRoleAssociations = (
  associations: NonNullable<WorkstreamEdit['employee_associations']>,
  roleId: string,
  employees: Pick<Employee, 'user_id'>[]
) => {
  const withoutRole = associations.filter((association) => association.dri !== roleId);
  const seen = new Set<string>();
  const roleAssociations = employees.flatMap((employee) => {
    if (seen.has(employee.user_id)) {
      return [];
    }

    seen.add(employee.user_id);
    return [{dri: roleId, user_id: employee.user_id}];
  });

  return [...withoutRole, ...roleAssociations];
};
